import { useState, useEffect, useMemo, useCallback, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import useAuth from 'src/hooks/useAuth';
import { Avatar, AvatarFallback, AvatarImage } from 'src/@/components/ui/avatar';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from 'src/@/components/ui/tooltip';
import { ScrollArea } from 'src/@/components/ui/scroll-area';
import Iconify from 'src/components/Iconify';
import { cn } from 'src/@/lib/utils';
import { setSessionStorageItem } from 'src/services/sessionStorage';
import { setSelectedChat, getChatInternoBuscarChat, getChatInternoChats } from 'src/redux/slices/staff';
import v2_actionChatInternoBuscarChat from 'src/actions/v2/chats/chat-interno/v2_actionChatInternoBuscarChat';
import NewChat from './components/NewChat';
import design from './ChatInternoGlobalSidebar.design';

function getInitials(name) {
  if (!name) return '';
  const names = name.split(' ');
  let initials = names[0].substring(0, 1).toUpperCase();
  if (names.length > 1) {
    initials += names[names.length - 1].substring(0, 1).toUpperCase();
  }
  return initials;
}

function getChatTitle(chat, userId) {
  if (chat?.isGroup) return chat.title || 'Grupo';
  const other = chat?.people?.find(p => p._id !== userId);
  if (!other) return 'Chat';
  return other.firstName
    ? `${other.firstName} ${other.lastName || ''}`.trim()
    : other.nomeFantasia || 'Chat';
}

function getChatAvatar(chat, userId) {
  if (chat?.isGroup) return null;
  const other = chat?.people?.find(p => p._id !== userId);
  return other?.photoURL || other?.avatar || null;
}

const ChatInternoGlobalSidebar = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const { user, cliente } = useAuth();
  const { staffs, roomsWithNewMessages, onlineUsers } = useSelector((state) => state.staff);

  const layoutMode = localStorage.getItem('layoutMode') || 'navbar';
  const isNavbar = layoutMode === 'navbar';

  const [isOpen, setIsOpen] = useState(() => {
    try {
      const saved = localStorage.getItem('chatInternoSidebarOpen');
      return saved !== null ? JSON.parse(saved) : true;
    } catch {
      return true;
    }
  });

  // Hide on chat interno pages
  const isHidden = pathname.startsWith('/chats/interno');

  // Fetch chats on mount if empty
  useEffect(() => {
    if (!staffs || staffs.length === 0) {
      if (cliente?._id && user?.id) {
        dispatch(getChatInternoChats(cliente._id, user.id));
      }
    }
  }, []);

  const toggleSidebar = useCallback(() => {
    setIsOpen(prev => {
      const next = !prev;
      localStorage.setItem('chatInternoSidebarOpen', JSON.stringify(next));
      window.dispatchEvent(new Event('chatInternoSidebarToggle'));
      return next;
    });
  }, []);

  const handleClickChat = useCallback(async (chat) => {
    try {
      const querys = `?limit=10&skip=0&msgs=1`;
      const response = await v2_actionChatInternoBuscarChat(chat._id, querys);
      if (response.status === 200) {
        setSessionStorageItem('chatInternoMiniId', response.data.room);
      }
      dispatch(getChatInternoBuscarChat(chat._id));
      dispatch(setSelectedChat(chat));
    } catch (err) {
      console.error('Erro ao abrir chat:', err);
    }
  }, [dispatch]);

  // Sort: unread first, then original order
  const sortedChats = useMemo(() => {
    if (!staffs || staffs.length === 0) return [];
    return [...staffs].sort((a, b) => {
      const aUnread = roomsWithNewMessages?.includes(a._id) ? 1 : 0;
      const bUnread = roomsWithNewMessages?.includes(b._id) ? 1 : 0;
      return bUnread - aUnread;
    });
  }, [staffs, roomsWithNewMessages]);

  if (isHidden) return null;

  return (
    <TooltipProvider delayDuration={200}>
      <div className={cn(design.wrapper, isOpen ? design.wrapperOpen : design.wrapperClosed, isNavbar && 'top-14 h-[calc(100vh-3.5rem)]')}>
        {/* Toggle button */}
        <button
          onClick={toggleSidebar}
          className={isOpen ? design.toggleButton : design.toggleButtonClosed}
          aria-label={isOpen ? t('chat.internal.sidebar.close') : t('chat.internal.sidebar.open')}
        >
          <Iconify
            icon={isOpen ? 'eva:chevron-right-outline' : 'eva:chevron-left-outline'}
            width={14}
            className={isOpen ? 'text-violet-500' : 'text-white'}
          />
        </button>

        {isOpen && (
          <div className={design.sidebar}>
            {/* Top accent glow line */}
            <div className={design.topAccent} />

            {/* New chat button */}
            <div className="flex items-center justify-center py-3 shrink-0">
              <NewChat
                icon
                listaChats={staffs || []}
                onCallback={() => {
                  if (cliente?._id && user?.id) {
                    dispatch(getChatInternoChats(cliente._id, user.id));
                  }
                }}
                customTrigger={
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#5d3a7f] to-[#2f1845] text-white flex items-center justify-center hover:shadow-lg hover:shadow-violet-500/25 hover:scale-105 active:scale-95 transition-all duration-200"
                      >
                        <Iconify icon="eva:plus-outline" width={20} />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="left" className={design.tooltipContent}>
                      {t('chat.internal.newInternalChat')}
                    </TooltipContent>
                  </Tooltip>
                }
              />
            </div>

            <div className="mx-auto w-5 h-[1px] bg-gray-200/80" />

            <ScrollArea className="flex-1">
              <div className={design.avatarList}>
                {sortedChats.map((chat, index) => {
                  const title = getChatTitle(chat, user?.id);
                  const avatarSrc = getChatAvatar(chat, user?.id);
                  const initials = getInitials(title);
                  const unreadCount = roomsWithNewMessages?.filter(id => id === chat._id).length || 0;
                  const other = chat?.people?.find(p => p._id !== user?.id);
                  const isOnline = !chat.isGroup && onlineUsers?.some(u => u === other?._id);

                  return (
                    <Tooltip key={chat._id}>
                      <TooltipTrigger asChild>
                        <div
                          className={design.avatarWrapper}
                          onClick={() => handleClickChat(chat)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => e.key === 'Enter' && handleClickChat(chat)}
                        >
                          <Avatar className={design.avatar}>
                            <AvatarImage src={avatarSrc} alt={title} className="object-cover rounded-xl" />
                            <AvatarFallback className={design.avatarFallback}>
                              {initials}
                            </AvatarFallback>
                          </Avatar>

                          {isOnline && <span className={design.onlineIndicator} />}

                          {chat.isGroup && (
                            <div className={design.groupIndicator}>
                              <Iconify icon="mingcute:group-3-fill" width={9} className="text-gray-400" />
                            </div>
                          )}

                          {unreadCount > 0 && (
                            <span className={design.unreadBadge}>
                              {unreadCount}
                            </span>
                          )}
                        </div>
                      </TooltipTrigger>
                      <TooltipContent side="left" className={design.tooltipContent}>
                        {title}
                      </TooltipContent>
                    </Tooltip>
                  );
                })}
              </div>
            </ScrollArea>
          </div>
        )}
      </div>
    </TooltipProvider>
  );
};

export default memo(ChatInternoGlobalSidebar);
